import React from 'react';
import ContractInfo from "./ContractInfo";
import DetailsPageHeader from "../../components/common/headers/DetailsPageHeader";
import {useLoaderData} from "react-router-dom";
import {usePreloadedQuery} from "react-relay";
import graphql from "babel-plugin-relay/macro";
import ContractLinesOfService from "./ContractLineOfService/ContractLinesOfService";
import ContractTerms from "./ContractTerms/ContractTerms";
import RosterLoadTable from "../roster-load/RosterLoadTable";
import {useUser} from "../../hooks/useUser";
import DeletePanel from "../../components/common/repeated-page-panels/DeletePanel";
import Attachments from "../../components/attachments/Attachments";
import {Col, Row} from "react-bootstrap";
import Notes from "../../components/notes/Notes";
import ContractPractitioners from "./ContractPractitioners";
import ContractBillingGroups from "./ContractBillingGroups";
import ContractFacilities from "./ContractFacilities";
import DataImporter from "./DataImporter";
import ContractPractitionersTable from "./ContractPractitionersTable";

const ContractDetails = () => {
    const loader = useLoaderData();
    const data = usePreloadedQuery(graphql`
        query ContractDetailsQuery($id: ID!) {
            node(id: $id) {
                ... on LegalContract {
                    id
                    name
                    startDate
                    endDate
                    network {
                        id
                        linesOfService {
                            ...LinesOfServiceFragments_LineOfServiceDropdown
                        }
                    }
                    ...ContractInfo_Contract
                    attachments {
                        ...Attachments
                        ...DataImporter_Attachments
                    }
                    linesOfService {
                        ...ContractLinesOfServiceFragments_LinesOfService
                    }
                    terms {
                        ...ContractTerms_Terms
                    }
                    rosterLoads {
                        ...RosterLoadTable_RosterLoad
                    }
                    notes {
                        ...Notes
                    }
                    billingGroups {
                        ...ContractBillingGroups_BillingGroups
                    }
                    facilities {
                        ...ContractFacilities_Facilities
                    }
                }
            }
            ...ContractPractitionersTableFragment @arguments(legalContractId: $id)
            allOrganizations {
                ...OrganizationsFragments_AllOrganizationsDropdown
            }
            allNetworks {
                ...NetworkFragments_allNetworkDropdown
            }
            allTermTypes {
                id
                description
                valueType
                ...TermTypesFragments_TermTypeDropdown
            }
            allAttachmentCategories {
                id
                name
                displayOrder
                ...Fragments_AllAttachmentCategoriesDropdown
            }
        }
    `, loader.contractDetailsQuery)
    const deleteMutation = graphql`
    mutation ContractDetailsDeleteMutation($id: ID!) {
        deleteNode(input: {id: $id}) {
                deletedNodeId @deleteRecord
        }}`;
    const {hasPermission} = useUser();
    return (
        <>
            <DetailsPageHeader iconText={"Contract"} iconType={"pen-nib"} title={data.node.name} />
            <Row className={"mb-3"}>
                <Col md={6}>
                    <ContractInfo contract={data.node} networks={data.allNetworks} organizations={data.allOrganizations} />
                </Col>
                {hasPermission("LegalContractNote.Read") && <Col>
                    <Notes parentId={data.node.id}
                           notes={data.node.notes}
                           createPermission={"LegalContractNote.Create"}
                           editPermission={"LegalContractNote.Update"}
                           deletePermission={"LegalContractNote.Delete"}
                           paginationSize={4}
                    />
                </Col>}
            </Row>
            {hasPermission("BillingGroup.Read") &&
                <ContractBillingGroups
                    billingGroups={data.node.billingGroups}
                />}
            {hasPermission("Practitioner.Read") &&
                <ContractPractitionersTable 
                    practitioners={data} legalContractId={data.node.id}
                />}
            {hasPermission("Facility.Read") &&
                <Row className={"mb-3"}>
                    <Col>
                        <ContractFacilities facilities={data.node.facilities}/>
                    </Col>
                </Row>
            }
            {hasPermission("RosterLoad.Read") && 
                <RosterLoadTable 
                    linesOfService={data.node.linesOfService} 
                    rosterLoad={data.node.rosterLoads}
                    allowNew={true}
                />}
            <ContractTerms terms={data.node.terms} contractId={data.node.id} possibleTermTypes={data.allTermTypes}/>
            {hasPermission("LegalContractAttachment.Read") && <Attachments attachments={data.node.attachments}
                                                                           parentId={data.node.id}
                                                                           attachmentCategories={data.allAttachmentCategories}
                                                                           createPermission={"LegalContractAttachment.Create"}
                                                                           deletePermission={"LegalContractAttachment.Delete"}
            />}
            <ContractLinesOfService linesOfService={data.node.linesOfService}
                                    contractId={data.node.id}
                                    possibleLinesOfService={data.node.network.linesOfService}
                                    contractStartDate={data.node.startDate}
                                    contractEndDate={data.node.endDate}
            />
            {hasPermission("Contract.Import.Data") && <DataImporter attachments={data.node.attachments}/>}
            {hasPermission("Contract.Delete") && 
                <DeletePanel mutation={deleteMutation} redirectUrl={"/contracts"} thingToDelete={"Contract"} id={data.node.id}>
                    In order to delete a Contract, there must be no data associated with it.
            </DeletePanel>
            }
        </>
    );
};

export default ContractDetails;
